import clsx from "clsx";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { basisLabel, fraction, fromNet, rateFor, roundCents, toCents, toNet, toNumber } from "../../shared/money";
import { useConfirm } from "../../web/components/Confirm";
import { ChangeLines, useLockedPriceConfirm } from "./PriceLock";
import { draftPrice, type ItemDraft, itemAsDrafted } from "./itemDrafts";
import { ProfitBox } from "./ProfitBox";

export type { ItemDraft } from "./itemDrafts";
import { FixHere } from "../../web/components/FixHere";
import { useDraftInvalid } from "../../web/components/fields";
import { DraftBar, ErrorNotice, Notice, SectionCard, Spinner } from "../../web/components/ui";
import { useMoney } from "../../web/lib/money";
import { can, type CostDraft, groupOf, groupTaxBasis, itemName, itemTax, membersOf, overridingIn, partsPriceOf, pricingGroupOf, quoteRate, type RowUpdate, updateEach, useGroupJobTime, useItemJobTime, usePriceListSaver, useRowUpdates, useStillShown } from "./data";
import { jobTimeShown, PricingEditor, type PriceMode } from "./PricingEditor";
import { quoteKey } from "./QuotedAmount";
import { describePricing } from "./describe";
import { GroupCardHeader } from "./GroupCardHeader";
import {
    type GroupTax,
    hasOwnRule,
    type ItemPricing,
    type MaterialRow,
    type PriceListData,
    pricedByGroup,
    priceLock,
    type PricingGroupRow,
    type PricingRule,
    type TaxQuote,
} from "./meta";
import { amountFor, estimatedPartCost, baseForPrice, type Basis, calcPrice, costWithLabour, exactMarkup, hasLabourCost, isPlain, itemProfit, keepShownAmounts, labourChanged, type NeedsCheck, needsCheck, partItem, percentFor, samePrice, sameRuleShown, shownAmount, storedAmount } from "./pricing";
import { plural } from "../../shared/words";

/*
 * An item's cost, tax and price. The price is typed in, or worked out from the cost (+ a % or an
 * amount), with optional labour and rounding. Pricing amounts are shown and typed in the item's
 * basis (with or without its tax), and stored before tax (see pricing.ts); the cost is typed as it's
 * quoted (QuotedAmount.tsx: "ex GST" until changed), or shown as the supplier price it follows is. Below: what it makes per
 * unit, before tax (ProfitBox).
 * In a group, the pricing is the group's, so it's locked here: it's changed in the group's
 * window (Open group: every item's new price shown before it's saved), or the item gets its own.
 * So is how it's taxed (the group says that too): on its own, the tax is chosen with its pricing.
 */

const ruleOnly = (p: PricingRule): PricingRule => ({
    mode: p.mode,
    markupValue: p.markupValue,
    basePrice: p.basePrice,
    labourMinutes: p.labourMinutes,
    labourUnit: p.labourUnit,
    roundMode: p.roundMode,
    roundTo: p.roundTo,
});

/** An item's own pricing as saved, as a draft (what an edit is compared with). */
function savedItemDraft(data: PriceListData, item: MaterialRow): ItemDraft {
    const pricing = data.itemPricing[item.uuid];
    return {
        rule: pricing && hasOwnRule(pricing) ? ruleOnly(pricing) : null,
        price: toCents(toNumber(item.price)),
        tax: { rateUuid: itemTax(data, item).uuid, includesTax: item.price_includes_taxes },
    };
}

/** A typed price (as typed, in `basis`) as the plain rule it stands for: a set price, nothing else. */
export const typedPriceRule = (price: string, basis: Basis): PricingRule => ({
    mode: "manual",
    markupValue: "0",
    basePrice: storedAmount(price, basis),
    labourMinutes: 0,
    labourUnit: "minutes",
    roundMode: "none",
    roundTo: "0",
});

/** A tax's basis, for comparing amounts as they're shown. */
const basisOfTax = (tax: TaxQuote, rates: PriceListData["taxRates"]): Basis => ({ includesTax: tax.includesTax, tax: fraction(rateFor(tax.rateUuid, rates)) });

/** Whether two drafts read the same on screen (pricing.ts sameRuleShown): an edit undone is no edit. A typed price is compared as the base price it is. */
function sameItemDraft(a: ItemDraft, b: ItemDraft, rates: PriceListData["taxRates"]) {
    if (quoteKey(a.tax, rates) !== quoteKey(b.tax, rates)) return false;
    const basis = basisOfTax(a.tax, rates);
    const ruleOf = (d: ItemDraft): PricingRule =>
        d.rule ?? typedPriceRule(d.price, basis);
    return sameRuleShown(ruleOf(a), ruleOf(b), basis);
}

/**
 * An item's pricing (its cost shown, set on its Costing tab): a draft while it's edited, like a
 * group's (`draft`, kept by the window: the box is marked, a bar shows the price it gives, Save or
 * Cancel, and closing asks first). Each time `reveal` goes up, the draft is scrolled into view.
 */
export function PricingTab(
    props: Readonly<{
        data: PriceListData;
        item: MaterialRow;
        draft: ItemDraft | null;
        onDraft: (draft: ItemDraft | null) => void;
        reveal: number;
        /** Open its Costing tab (where the cost is set). */
        onCostTab: () => void;
        /** Open its Parts tab (a kit's parts). */
        onPartsTab: () => void;
        /** Its price warnings in full, attached to what prices it: the group's card, or its own price settings. */
        fix?: ReactNode;
        /** Bumped by a warning's Show: the price warnings (`fix`), or what it makes, scrolled to and flashed. */
        fixToken?: number;
        profitToken?: number;
        /** Its cost being changed (its Costing tab's draft): what it makes is shown with it. */
        costDraft: CostDraft | null;
    }>,
) {
    const { data, item } = props;
    const canEdit = can(data, "items.pricing");
    const pricing = data.itemPricing[item.uuid];
    // How long it usually takes on jobs (its Stats tab's), shown by Labour: with See stats. On too few jobs for its own, its group's.
    const seesStats = can(data, "stats");
    const ownTime = useItemJobTime(item.uuid, seesStats);
    const inGroup = groupOf(data, item.uuid);
    const groupTime = useGroupJobTime(inGroup?.id ?? "", seesStats && inGroup !== undefined && ownTime !== null && ownTime.first === null && !ownTime.leftOut);
    const jobTime = jobTimeShown(ownTime, inGroup ? { time: groupTime, name: inGroup.name } : undefined);
    const box = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (props.reveal) box.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [props.reveal]);
    // The group pricing it (when its group sets pricing: else it's priced its own way, as out of a group).
    const group = pricingGroupOf(data, item.uuid);
    // Overriding its group's price (or starting to: its Override pressed): its own pricing sits in the group's blue panel (its save bar with it).
    const overridesGroup = group !== undefined && (pricing?.overridesGroup === true || props.draft?.override === true);

    // The pricing as edited (the draft), else as saved. A change back to what's saved is no change.
    const saved = savedItemDraft(data, item);
    const d = props.draft ?? saved;
    const edit = (next: ItemDraft) => {
        // Starting an override, it stays a draft (to save or cancel) even with nothing changed yet.
        props.onDraft(sameItemDraft(next, saved, data.taxRates) && !next.override ? null : next);
        return Promise.resolve();
    };
    const taxNow = quoteRate(data, d.tax);
    const basis: Basis = { includesTax: d.tax.includesTax, tax: taxNow.tax };
    const now = itemAsDrafted(data, item, d);
    const priceNow = draftPrice(data, item, d);
    // What its rounding changes the price by (at least half a cent), in its basis.
    const drafted = d.rule ? calcPrice(now.item, now.tax, { ...d.rule, partsPrice: partsPriceOf(data, item.uuid) }, data.settings) : null;
    const roundedBy = drafted ? roundCents(drafted.rounded - drafted.total) : 0;

    /** The rule as edited, or a plain one standing for the typed price (so extras can be added to it). */
    const current: PricingRule = d.rule ?? typedPriceRule(d.price, basis);
    /** A change to its rule: back to a typed price when nothing's left to work out. */
    const saveRule = (patch: Partial<PricingRule>) => {
        const next = { ...current, ...patch };
        // Nothing to keep: a typed price (labour shown in hours is kept even with none yet, so choosing hours sticks).
        // Overriding its group, it keeps a rule (a set price): a typed price would take it out of the group.
        // In a group (priced its own way there), it keeps a rule: a typed price would take it out of the group.
        const nothingToKeep = isPlain(next) && next.labourUnit === "minutes" && !pricing?.groupId;
        return edit(nothingToKeep ? { ...d, rule: null, price: shownAmount(next.basePrice, basis) } : { ...d, rule: next });
    };
    /** Another tax: typed amounts stay as typed, read the new way (pricing.ts keepShownAmounts). */
    const changeTax = (tax: TaxQuote) => {
        const to = quoteRate(data, tax);
        const toBasis = { includesTax: tax.includesTax, tax: to.tax };
        return edit({ rule: d.rule ? keepShownAmounts(d.rule, basis, toBasis) : null, price: d.price, tax });
    };

    // Worked out from the cost: not without one (pricing.ts missingCost).
    const noCost = toNumber(now.item.cost) <= 0 ? "Cost + % and Cost + amount need a cost: set one on the Costing tab." : undefined;

    // The same markup the other way, stored exactly and only rounded as it's shown (a % to 2 places, an
    // amount to the cent): switching back and forth never drifts ($60 -> 109.09% -> $60).
    const switchSource = (to: PriceMode) => {
        // A kit's parts' prices: on top of them, nothing to start with (each part's price is already marked up).
        if (to === "parts") return saveRule({ mode: "parts", markupValue: "0" });
        const { costNet, markupNet } = exactMarkup(now.item, now.tax, d.rule ? { ...d.rule, partsPrice: partsPriceOf(data, item.uuid) } : undefined, data.settings);
        if (to === "percent") return saveRule({ mode: "percent", markupValue: percentFor(markupNet, costNet) });
        if (to === "fixed") return saveRule({ mode: "fixed", markupValue: amountFor(markupNet) });
        // Set price: keep the price it gives now, and any labour (which keeps following the labour rate).
        return saveRule({ mode: "manual", basePrice: baseForPrice(now.item, now.tax, priceNow, current.labourMinutes, data.settings), markupValue: "0" });
    };

    // Its own pricing, edited as a draft in the editor groups use too (PricingEditor.tsx).
    const ruleFields = (
        <PricingEditor
            data={data}
            rule={current}
            tax={d.tax}
            canEdit={canEdit}
            price={priceNow}
            costBasedUnavailable={noCost}
            // What a % markup is on: the part's cost before tax, taxed as the draft is.
            partCostNet={toNet(toNumber(now.item.cost), now.item.price_includes_taxes, now.tax)}
            estimatedPartNet={itemProfit(data, item, itemTax(data, item).tax).partNet}
            onCostTab={props.onCostTab}
            onPartsTab={props.onPartsTab}
            roundedBy={roundedBy}
            // A kit: its parts' prices too (none yet: it can't use them).
            partsPrice={item.uuid in data.kitParts ? partsPriceOf(data, item.uuid) : undefined}
            editing={props.draft !== null}
            jobTime={jobTime}
            tinted={overridesGroup}
            // Being edited: its save bar is where its price warning's Show brings you (FixHere.tsx: the standard).
            footer={
                props.draft ? (
                    <FixHere attached token={props.fixToken} className="overflow-visible rounded-none">
                        <ItemSave data={data} item={item} draft={props.draft} tinted={overridesGroup} onDone={() => props.onDraft(null)} />
                    </FixHere>
                ) : null
            }
            boxRef={box}
            onRule={saveRule}
            onMode={switchSource}
            onTax={changeTax}
        />
    );

    // Priced by its group (not overriding it): the group's pricing in the same fields, greyed (changed on the group, or by
    // overriding it here: Override starts from these), so how its price is made is read the same as an item's own.
    const groupFields = (() => {
        if (!group || overridesGroup || !pricing) return null;
        const g: ItemDraft = {
            rule: ruleOnly(pricing),
            price: toCents(toNumber(item.price)),
            tax: pricing.groupTax ? { rateUuid: pricing.groupTax.rateUuid, includesTax: pricing.groupTax.includesTax } : saved.tax,
        };
        const gNow = itemAsDrafted(data, item, g);
        const gCalc = g.rule ? calcPrice(gNow.item, gNow.tax, { ...g.rule, partsPrice: partsPriceOf(data, item.uuid) }, data.settings) : null;
        const none = () => Promise.resolve();
        return (
            <PricingEditor
                data={data}
                rule={ruleOnly(pricing)}
                tax={g.tax}
                canEdit={false}
                price={draftPrice(data, item, g)}
                partCostNet={toNet(toNumber(gNow.item.cost), gNow.item.price_includes_taxes, gNow.tax)}
                estimatedPartNet={itemProfit(data, item, itemTax(data, item).tax).partNet}
                onCostTab={props.onCostTab}
                onPartsTab={props.onPartsTab}
                roundedBy={gCalc ? roundCents(gCalc.rounded - gCalc.total) : 0}
                partsPrice={item.uuid in data.kitParts ? partsPriceOf(data, item.uuid) : undefined}
                editing={false}
                jobTime={jobTime}
                onRule={none}
                onMode={none}
                onTax={none}
            />
        );
    })();

    // Its tab's height (RecordWindow), so what it makes per unit (ProfitBox) sits at the bottom of the window when there's room.
    return (
        <div className="flex flex-1 flex-col">
            <section>
                <PricingBox
                    data={data}
                    item={item}
                    group={group}
                    overriding={overridesGroup}
                    // Override: its own pricing, starting from the group's (the same price), as a draft to save or cancel.
                    onOverride={pricing ? () => props.onDraft({ ...saved, rule: ruleOnly(pricing), override: true }) : undefined}
                    onLeave={() => props.onDraft(null)}
                    fix={props.fix}
                    fixToken={props.fixToken}
                    groupFields={groupFields}
                >
                    {ruleFields}
                </PricingBox>
            </section>

            {/* At the bottom (of the window when there's room): what it makes per unit, its drafts (this one, its cost's) as they'd make it. */}
            <ProfitBox data={data} item={item} draft={props.draft} costDraft={props.costDraft} fixToken={props.profitToken} />
        </div>
    );
}

/**
 * Saving an item's pricing draft: the price it gives (now -> new, with the tax when that changes),
 * then save (with a rule: updating the price, or leaving it to update) or cancel. Like a group's.
 */
function ItemSave(props: Readonly<{ data: PriceListData; item: MaterialRow; draft: ItemDraft; /** On the overriding panel: a blue line above it. */ tinted?: boolean; onDone: () => void }>) {
    const { data, item, draft } = props;
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const saved = savedItemDraft(data, item);
    const taxChanged = quoteKey(draft.tax, data.taxRates) !== quoteKey(saved.tax, data.taxRates);
    // Its labour costing another amount with this pricing: its cost will need updating too, its own step (Cost to update).
    const withDraft = { ...data, itemPricing: { ...data.itemPricing, [item.uuid]: { ...data.itemPricing[item.uuid], labourMinutes: draft.rule?.labourMinutes ?? 0 } as ItemPricing } };
    // On its part's cost as it'll be (a cost to update's: what Save & update writes, as the server works it out).
    const partWillCost = estimatedPartCost(data, item);
    const costTo = labourChanged(withDraft, item.uuid) ? costWithLabour(withDraft, item, partWillCost) : null;
    // Its price and cost updated together (below: its tax the same), the price is worked out from its part's cost as it'll be.
    const together = costTo !== null && can(data, "costs.edit") && draft.rule !== null && !taxChanged;
    const next = draftPrice(data, item, draft, together ? partWillCost : undefined);
    const priceMoves = !samePrice(next, item.price);
    const moves = taxChanged || priceMoves;
    const labelNow = basisLabel(item.price_includes_taxes, itemTax(data, item).rate);
    const labelNext = basisLabel(draft.tax.includesTax, quoteRate(data, draft.tax).rate);
    // Something typed above can't be taken (the draft has what it had before): not saved until it's put right.
    const typedBad = useDraftInvalid();
    // Its labour changes what it costs: its cost can be updated with it (as its main button says), for those who may change costs.
    const withCost = costTo !== null && can(data, "costs.edit");

    const confirmLocked = useLockedPriceConfirm(data);
    const save = async (updatePrice: boolean, updateCost = false) => {
        // A typed price left for later is saved as a set price (a rule), so the item shows the new one as a price to update.
        const basis = { includesTax: draft.tax.includesTax, tax: quoteRate(data, draft.tax).tax };
        const later = typedPriceRule(draft.price, basis);
        const rule = draft.rule ?? (draft.override || (priceMoves && !updatePrice) ? later : null);
        // Its price or tax changes with this save (not when the price is left to update): locked, asked first.
        const changesPrice = taxChanged || (priceMoves && (updatePrice || rule === null));
        // Asked with all it changes: its price, and its cost too when that's updated with it.
        const change = (
            <ChangeLines
                price={{ from: item.price, to: next, ...(taxChanged ? { fromLabel: labelNow, toLabel: labelNext } : {}) }}
                cost={updateCost && costTo ? { from: item.cost, to: costTo, ...(taxChanged ? { label: labelNow } : {}) } : undefined}
            />
        );
        const answer = changesPrice ? await confirmLocked(item, change) : "unlocked";
        if (answer === "cancelled") return;
        setBusy(true);
        setError(null);
        // Its price and cost both updated, its tax the same: its pricing saved, then both in one ServiceM8 save (neither without the other).
        const bothAtOnce = updatePrice && updateCost && rule !== null && !taxChanged;
        try {
            await saver.saveItemPricing(item.uuid, {
                rule,
                ...(taxChanged ? { tax: draft.tax } : {}),
                ...(rule === null ? { price: draft.price } : {}),
                updatePrice: updatePrice && !bothAtOnce,
                confirmLocked: answer === "confirmed",
                ...(draft.override ? { overrideGroup: true } : {}),
            });
            if (bothAtOnce) {
                await saver.updatePriceAndCost(item.uuid, answer === "confirmed");
                props.onDone();
                return;
            }
        } catch (err) {
            setError(err as Error);
            setBusy(false);
            return;
        }
        try {
            // Its cost after (its new labour saved): what ServiceM8 gets is worked out from it (supplierPrices.ts updateCost).
            if (updateCost) await saver.updateCost(item.uuid);
            props.onDone();
        } catch (err) {
            // Its pricing is saved: the bar stays to say so (its cost still to update, on its Costing tab).
            setError(new Error(`Its pricing is saved, but its cost couldn't be updated: ${(err as Error).message}`));
        } finally {
            setBusy(false);
        }
    };

    return (
        // Like a warning strip (FixHere attached): along the bottom of the editor, edge to edge, the same size; what saving does on
        // the left (wrapping as it needs), its buttons on the right (under it only when narrow).
        <div className={clsx("-mx-4 -mb-4 border-t px-3 py-2", props.tinted ? "border-override/30" : "border-brand-500/30")}>
            <DraftBar
                message={
                    <>
                        {/* What it changes (as its question says it, if it asks): its price, and its labour's cost on a line of its own. */}
                        {moves ? null : "The price doesn't change."}
                        <ChangeLines
                            price={moves ? { from: item.price, to: next, ...(taxChanged ? { fromLabel: labelNow, toLabel: labelNext } : {}) } : undefined}
                            cost={costTo !== null ? { from: item.cost, to: costTo, ...(taxChanged ? { label: labelNow } : {}) } : undefined}
                        />
                        {costTo !== null && !withCost ? <span className="block text-xs">Its cost is to update on its Costing tab.</span> : null}
                        {typedBad ? <span className="block text-xs text-danger">Put right what&apos;s marked above to save.</span> : null}
                    </>
                }
                busy={busy}
                error={error}
                actions={
                    <>
                        {priceMoves ? (
                            // Update now, or leave the price to update: always in that order (a locked price is asked about first, held).
                            <div className="flex flex-wrap items-center gap-1.5">
                                <button
                                    type="button"
                                    className="btn-primary"
                                    disabled={busy || typedBad}
                                    title={`Save the pricing and change the price to ${fmt.money(next)}${withCost && costTo ? `, and the cost to ${fmt.money(costTo)}` : ""}`}
                                    onClick={() => void save(true, withCost)}
                                >
                                    Save &amp; update
                                </button>
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    disabled={busy || typedBad}
                                    title={`Save the pricing only: the price stays ${fmt.money(item.price)}${withCost ? " and so does the cost" : ""}, and ${withCost ? "they show" : "it shows"} as to update until updated`}
                                    onClick={() => void save(false)}
                                >
                                    Save only
                                </button>
                            </div>
                        ) : withCost && costTo ? (
                            // Its price the same, its cost not (its labour): update it now, or leave it to update.
                            <>
                                <button type="button" className="btn-primary" disabled={busy || typedBad} title={`Save the pricing and change the cost to ${fmt.money(costTo)}`} onClick={() => void save(false, true)}>
                                    Save &amp; update
                                </button>
                                <button type="button" className="btn-secondary" disabled={busy || typedBad} title="Save the pricing only: the cost shows as to update until it's updated" onClick={() => void save(false)}>
                                    Save only
                                </button>
                            </>
                        ) : (
                            <button type="button" className="btn-primary" disabled={busy || typedBad} onClick={() => void save(false)}>
                                Save pricing
                            </button>
                        )}
                        <button type="button" className="btn-secondary" disabled={busy} onClick={props.onDone}>
                            Cancel
                        </button>
                    </>
                }
            />
        </div>
    );
}

/**
 * How the price is worked out, in one box: its group's card (shared, so only shown here: it's edited
 * in the group's window, where all its items are), or its own fields, with New group to start one from
 * them. Its group is picked on its Details tab (it sets its name tags too). Outside the box, the item's
 * own things (tax, cost, what it makes).
 */
function PricingBox(
    props: Readonly<{
        data: PriceListData;
        item: MaterialRow;
        group: PricingGroupRow | undefined;
        /** Overriding its group's price, or starting to (a draft): its own pricing shown, under the group's card. */
        overriding: boolean;
        /** Start overriding its group's price (a draft); none: it can't (nothing to start from). */
        onOverride?: () => void;
        /** Back to its group's price: its own pricing's draft (if any) goes. */
        onLeave: () => void;
        /** Its price warnings, attached to what prices it (FixHere), and Show's token. */
        fix?: ReactNode;
        fixToken?: number;
        /** Priced by its group: the group's pricing, read-only, in the group's card in place of its own fields. */
        groupFields?: ReactNode;
        children: ReactNode;
    }>,
) {
    const { data, item, group } = props;
    // The group it's in, whatever it sets: one not setting pricing is said on its own pricing's card.
    const member = groupOf(data, item.uuid);
    return (
        <section aria-label="How the price is worked out" className="box">
            {group ? (
                <>
                    {/* Overriding: its own pricing sits inside the group's card, under it, as one blue panel. */}
                    <GroupCard
                        data={data}
                        item={item}
                        group={group}
                        overriding={props.overriding}
                        onOverride={props.onOverride}
                        onLeave={props.onLeave}
                        fix={
                            props.fix ? (
                                <FixHere attached token={props.fixToken}>
                                    {props.fix}
                                </FixHere>
                            ) : undefined
                        }
                    >
                        {props.overriding ? props.children : props.groupFields}
                    </GroupCard>
                </>
            ) : (
                // Priced its own way: the same card, its warning along its bottom (in a group that doesn't set pricing, saying so).
                <PricingCard
                    aside={member && !member.setsPricing ? `Its group (${member.name}) doesn't set pricing.` : undefined}
                    fix={
                        props.fix ? (
                            <FixHere attached token={props.fixToken}>
                                {props.fix}
                            </FixHere>
                        ) : undefined
                    }
                >
                    {props.children}
                </PricingCard>
            )}
        </section>
    );
}

/**
 * Pricing's card, the same wherever pricing is set (an item's own, an item in a group, a group's), as Name tags' box is: "Pricing"
 * above it (with `aside` on the right), any `header` along its top (an item's group: its card), the editor in it (`children`,
 * padded so its editing tint and save bar reach its edges), and along its bottom its warnings (`fix`). `overriding`: an item
 * overriding its group's price, so its border and body are blue (its header says so too).
 */
export function PricingCard(props: Readonly<{ aside?: ReactNode; header?: ReactNode; overriding?: boolean; fix?: ReactNode; children?: ReactNode }>) {
    // Its body padded as the editor's editing tint and save bar expect (PricingEditor.tsx), so they reach its edges.
    return (
        <SectionCard label="Pricing" aside={props.aside} header={props.header} tone={props.overriding ? "override" : undefined} bodyClassName="px-4 pt-3.5 pb-4" fix={props.fix}>
            {props.children}
        </SectionCard>
    );
}

/**
 * In a group: its pricing card, headed by the group (GroupCardHeader, as its name tags' card): what the group sets, and
 * Override, which opens this item's own pricing under it as a draft (starting from the group's: saving makes it an override,
 * Cancel doesn't), the card blue; overriding, Use group's price (asked first: its own pricing goes) instead.
 */
function GroupCard(
    props: Readonly<{
        data: PriceListData;
        item: MaterialRow;
        group: PricingGroupRow;
        /** Overriding its price, or starting to (a draft, not saved yet). */
        overriding: boolean;
        /** Start overriding (a draft); none: it can't. */
        onOverride?: () => void;
        /** Back to the group's price: its own pricing's draft (if any) goes. */
        onLeave: () => void;
        /** Overriding: its own pricing, inside the card under its header (one panel). */
        children?: ReactNode;
        /** Its price warnings (FixHere attached), along the card's bottom. */
        fix?: ReactNode;
    }>,
) {
    const { data, item, group, overriding } = props;
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const confirm = useConfirm();
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const taxed = groupTaxBasis(data, group.tax);
    // Overriding as saved (not just started: a draft, cancelled with its save bar).
    const saved = Boolean(data.itemPricing[item.uuid]?.overridesGroup);
    const canEdit = can(data, "items.pricing");
    // Back to the group's price: asked first (its own pricing goes).
    const useGroups = async () => {
        const yes = await confirm({
            title: `Use the ${group.name} group's price?`,
            subject: itemName(data, item),
            message: "Its own pricing goes, and it's priced and taxed as the group says. If that gives another price, it shows as a price to update.",
            confirmLabel: "Use the group's price",
            tone: "primary",
        });
        if (!yes) return;
        props.onLeave();
        setError(null);
        setBusy(true);
        saver
            .setOverride(item.uuid, false)
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    // Its header along the top of the pricing card (overriding: its own pricing under it, the card blue).
    const header = (
        // Over something (its own pricing overriding it, blue; or the group's, read-only): a line under it.
        <div className={clsx("space-y-2 px-3 py-2.5", overriding ? "border-b border-override/30 bg-override-header" : props.children ? "border-b border-line bg-surface-2" : undefined)}>
            <GroupCardHeader
                group={group}
                badge={saved ? { text: "Price overridden", title: "This item is priced its own way (below), not by the group: changes to the group's pricing don't reach it" } : undefined}
                summary={`${describePricing(group.rule, taxed, fmt)} · ${basisLabel(taxed.includesTax, taxed.rate)}`}
                opens="its pricing and all its items"
                actions={
                    // Override (its own pricing, a draft: its save bar saves or cancels it), or overriding, back to the group's.
                    <>
                        {canEdit && !overriding && props.onOverride ? (
                            <button type="button" className="btn-secondary" title="Price it its own way, starting from the group's pricing: save to override, or cancel" onClick={props.onOverride}>
                                Override
                            </button>
                        ) : null}
                        {canEdit && saved ? (
                            <button type="button" className="btn-secondary" disabled={busy} title="Its own pricing goes: priced by the group again (asked first)" onClick={() => void useGroups()}>
                                Use group&apos;s price
                            </button>
                        ) : null}
                    </>
                }
            />
            <ErrorNotice error={error} />
        </div>
    );
    return (
        <PricingCard header={header} overriding={overriding} fix={props.fix}>
            {props.children}
        </PricingCard>
    );
}

/**
 * Its pricing gives a different price than it has: update it, or keep the one it has (not in a
 * group: to keep another price, it's taken out of the group). Along the bottom of what prices it (PricingBox).
 */
export function CheckNotice({ data, item }: Readonly<{ data: PriceListData; item: MaterialRow }>) {
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const confirmLocked = useLockedPriceConfirm(data);
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const check = needsCheck(partItem(data, item), itemTax(data, item), data.itemPricing[item.uuid], data.settings);
    if (!check) return null;
    const group = groupOf(data, item.uuid);
    // Overriding its group, it's checked against its own pricing.
    const byGroup = group && pricedByGroup(data.itemPricing[item.uuid]) ? group : undefined;
    // Attached to what prices it (the group's card, or its own settings): which, in a word.
    const pricing = byGroup ? "The group" : "This pricing";
    // Its group taxes it otherwise: the new price is in that basis, and updating sets it.
    const target = check.taxTo ? `${fmt.money(check.target)} ${basisLabel(check.taxTo.includesTax, groupTaxBasis(data, check.taxTo).rate)}` : fmt.money(check.target);
    const message = checkMessage(data, item, check, pricing, target, fmt.money);
    const run = (fn: () => Promise<unknown>) => {
        setBusy(true);
        setError(null);
        fn()
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    return (
        <Notice
            tone="warning"
            title="Price to update:"
            actions={
                can(data, "items.pricing") ? (
                    <>
                        <button
                            type="button"
                            className="btn-primary"
                            disabled={busy}
                            onClick={() =>
                                // Locked: asked first.
                                void confirmLocked(item, <ChangeLines price={{ from: item.price, to: check.target }} />).then(
                                    (answer) => answer !== "cancelled" && run(() => saver.updateToPricing(item.uuid, answer === "confirmed")),
                                )
                            }
                        >
                            Update to {target}
                        </button>
                        {/* In a group, keeping another price means overriding the group's (it stays in it). */}
                        <button
                            type="button"
                            className="btn-secondary"
                            disabled={busy}
                            title={byGroup ? `Keep this price as its own, overriding the ${byGroup.name} group's: it stays in the group` : undefined}
                            onClick={() => run(() => saver.keepPrice(item.uuid))}
                        >
                            Keep {fmt.money(item.price)}
                            {byGroup ? " (override)" : ""}
                        </button>
                    </>
                ) : null
            }
        >
            {/* The change on its title's line (now -> new, as its save bar says it), why on the next. */}
            <span className="tabular-nums">
                {fmt.money(item.price)} → <strong>{target}</strong>
            </span>
            <span className="block">{message}</span>
            <ErrorNotice error={error} />
        </Notice>
    );
}

/**
 * Why its price needs updating, short (it sits beside its buttons): "The group gives $123.00, not $180.00." When its group taxes it otherwise, both prices say how they're
 * taxed (and the price now, the new way too), so "$45.46" against "$50.00 inc GST" doesn't read as a
 * jump: it's the same price, switching to inc GST.
 */
function checkMessage(data: PriceListData, item: MaterialRow, check: NeedsCheck, pricing: string, target: string, money: (v: string | number) => string) {
    if (!check.taxTo) {
        return check.reason === "edited"
            ? `Changed to ${money(item.price)} in ServiceM8. ${pricing} gives ${target}.`
            : `${pricing} gives ${target}, not ${money(item.price)}.`;
    }
    const { rate, tax } = itemTax(data, item);
    const to = groupTaxBasis(data, check.taxTo);
    const toLabel = basisLabel(to.includesTax, to.rate);
    const nowTheNewWay = fromNet(toNet(toNumber(item.price), item.price_includes_taxes, tax), to.includesTax, to.tax);
    return `${pricing} gives ${target}, not ${money(item.price)} ${basisLabel(item.price_includes_taxes, rate)} (${money(nowTheNewWay)} ${toLabel}).`;
}

/** "Making a loss", "Making nothing" or "Making too little" (on its cost): its pill in Needs attention (its figures on the Pricing tab say how much). */
export function profitTitle(data: PriceListData, item: MaterialRow) {
    const net = itemProfit(data, item, itemTax(data, item).tax).profitNet ?? 0;
    if (net < 0) return "Making a loss";
    return net === 0 ? "Making nothing" : "Making too little";
}


/** A group's pricing being changed (in its window), not saved yet: its rule and how it taxes its items. */
export interface GroupDraft {
    rule: PricingRule;
    tax: GroupTax;
}

/** Whether a draft is just the group's pricing as saved (whatever order its fields were set in). */
export function sameDraft(draft: GroupDraft, group: PricingGroupRow, rates: PriceListData["taxRates"]) {
    if (quoteKey(draft.tax, rates) !== quoteKey(group.tax, rates)) return false;
    return sameRuleShown(draft.rule, group.rule, basisOfTax(draft.tax, rates));
}

/** Each item in the group that the draft would change: its price now, and the price (and tax, if that changes) it would give. */
function groupPreview(data: PriceListData, groupId: string, draft: GroupDraft) {
    const { rate: _rate, ...groupTax } = groupTaxBasis(data, draft.tax);
    const pricing: ItemPricing = { ...draft.rule, appliedPrice: null, groupId, groupTax, overridesGroup: false, tagOverrides: {} };
    // Not those overriding its price: the group's pricing doesn't reach them.
    return membersOf(data, groupId).flatMap((m) => {
        if (!pricedByGroup(data.itemPricing[m.uuid])) return [];
        const itemRate = itemTax(data, m);
        const check = needsCheck(partItem(data, m), itemRate, pricing, data.settings);
        if (!check) return [];
        // The tax it has now and would have: shown when they differ.
        const taxNow = basisLabel(m.price_includes_taxes, itemRate.rate);
        const taxNext = check.taxTo ? basisLabel(check.taxTo.includesTax, groupTaxBasis(data, check.taxTo).rate) : taxNow;
        return [{ m, now: m.price, next: check.target, taxNow, taxNext }];
    });
}

/**
 * Asking before a group's items are updated together (its save bars' Save & update, its Pricing and Name tags tabs' Update buttons,
 * its Items tab's ticked ones): how many, and to what, each a change in ServiceM8. Locked prices among them: said, and held to
 * confirm. `saving`: its pricing or name tags are saved first (the button says so). True: go ahead.
 */
export function useGroupUpdateConfirm(data: PriceListData) {
    const confirm = useConfirm();
    const lockedIn = (uuids: readonly string[]) => uuids.filter((u) => priceLock(data, u) !== null).length;
    const ask = (things: string, message: string, opts: { locked?: number; saving?: boolean } = {}) =>
        confirm({
            title: `Update ${things}?`,
            message: `${message} Each is changed in ServiceM8.`,
            ...(opts.locked
                ? {
                      detail: `${opts.locked === 1 ? "One of them is locked" : `${opts.locked} of them are locked`}: locked prices only change once confirmed.`,
                      detailTone: "warning" as const,
                      hold: true,
                  }
                : {}),
            confirmLabel: opts.saving ? "Save & update" : `Update ${things}`,
        });
    const priceText = (n: number) => `${n === 1 ? "Its price goes" : "Their prices go"} to what the group gives.`;
    const costText = (n: number) => `${n === 1 ? "Its cost goes" : "Their costs go"} to what ${n === 1 ? "its" : "their"} labour, supplier price or parts give.`;
    const nameText = (n: number) => `${n === 1 ? "Its name gets" : "Their names get"} the group's name tags.`;
    return {
        prices: (uuids: readonly string[]) => ask(itemCount(uuids.length, "price"), priceText(uuids.length), { locked: lockedIn(uuids) }),
        costs: (n: number) => ask(itemCount(n, "cost"), costText(n)),
        names: (n: number, saving = false) => ask(itemCount(n, "name"), nameText(n), { saving }),
        /** Its pricing saved, then these prices and costs (either can be none). */
        saveAndUpdate: (prices: readonly string[], costs: number) => {
            const things = [prices.length ? itemCount(prices.length, "price") : "", costs ? itemCount(costs, "cost") : ""].filter(Boolean).join(" and ");
            const message = [prices.length ? priceText(prices.length) : "", costs ? costText(costs) : ""].filter(Boolean).join(" ");
            return ask(things, message, { locked: lockedIn(prices), saving: true });
        },
    };
}

/**
 * Not being edited: its prices that need updating now (what its pricing gives isn't what they are), and the costs to update of
 * those it prices (e.g. its labour saved without them), each now -> new: updated here, all of them (each a ServiceM8 write, as the
 * person clicking; locked prices asked about first, once), or some, ticked on its Items tab. None: nothing.
 */
export function GroupPricesToUpdate({ data, group, onItems }: Readonly<{ data: PriceListData; group: PricingGroupRow; onItems: () => void }>) {
    const saver = usePriceListSaver();
    const ask = useGroupUpdateConfirm(data);
    const [progress, setProgress] = useState<string | null>(null);
    const [error, setError] = useState<Error | null>(null);
    // Each row's update as it goes: marked in the list (the one being saved, those waiting), gone once done.
    const updates = useRowUpdates();
    const rows = groupPreview(data, group.id, { rule: group.rule, tax: group.tax });
    const costs = new Map<string, CostChange>();
    for (const m of membersOf(data, group.id)) {
        const next = data.costUpdates[m.uuid];
        // Every member's (as its Needs attention and Items tab count them), not only those it prices.
        if (next !== undefined) costs.set(m.uuid, { m, now: m.cost, next });
    }
    if (!rows.length && !costs.size) return null;
    const things = [rows.length ? itemCount(rows.length, "price") : "", costs.size ? itemCount(costs.size, "cost") : ""].filter(Boolean).join(" and ");
    const canPrices = can(data, "groups") && rows.length > 0;
    const canCosts = can(data, "costs.edit") && costs.size > 0;
    const busy = progress !== null;
    const run = (uuids: string[], update: (uuid: string) => Promise<unknown>, what: string) => {
        setError(null);
        updates.clear();
        updateEach(uuids, update, what, setProgress, updates.track(what))
            .catch((err: Error) => setError(err))
            .finally(updates.settle);
    };
    // Each asked first: they change many items at once.
    const updatePrices = async () => {
        const uuids = rows.map((r) => r.m.uuid);
        if (await ask.prices(uuids)) run(uuids, (uuid) => saver.updateToPricing(uuid, priceLock(data, uuid) !== null), "prices");
    };
    const updateCosts = async () => {
        if (await ask.costs(costs.size)) run([...costs.keys()], saver.updateCost, "costs");
    };
    const canUpdate = canPrices || canCosts;
    // In the save bar's place (the editor's footer, after Save only), laid out as it is: the changes, then along the bottom of the
    // card (edge to edge) what to do with them.
    return (
        <>
            <PriceChanges data={data} rows={rows} costs={costs} updates={updates.rows} />
            <div className="-mx-4 -mb-4 border-t border-line px-3 py-2">
                <DraftBar
                    // What's to update (as the save bar says what saving does), and where to update only some. Updating: just how far it's
                    // got, on one line (the list shows which).
                    message={
                        progress ? (
                            <span className="inline-flex items-center gap-2">
                                <Spinner className="size-4" /> {progress}
                            </span>
                        ) : (
                            <>
                                {things} {rows.length + costs.size === 1 ? "needs" : "need"} updating.
                                <span className="block text-xs">
                                    {canUpdate ? "Choose which to update on the " : "They're updated from the "}
                                    <button type="button" className="link-button text-xs" onClick={onItems}>
                                        Items tab
                                    </button>
                                    .
                                </span>
                            </>
                        )
                    }
                    error={error}
                    actions={
                        <>
                            {/* The same size as the save bar's: it's in its place. */}
                            {canPrices ? (
                                <button type="button" className="btn-primary" disabled={busy} title={`Update ${plural(rows.length, "price")} to what the group gives`} onClick={() => void updatePrices()}>
                                    Update {rows.length === 1 ? "price" : `${rows.length} prices`}
                                </button>
                            ) : null}
                            {canCosts ? (
                                <button
                                    type="button"
                                    className="btn-primary"
                                    disabled={busy}
                                    title={`Update ${plural(costs.size, "cost")} to what ${costs.size === 1 ? "its" : "their"} labour, supplier price or parts give`}
                                    onClick={() => void updateCosts()}
                                >
                                    Update {costs.size === 1 ? "cost" : `${costs.size} costs`}
                                </button>
                            ) : null}
                        </>
                    }
                />
            </div>
        </>
    );
}

/**
 * Saving a group's pricing draft, like an item's: the prices it would move, then its save bar: Save & update them
 * (locked ones asked about first), Save only (they show as to update on its Items tab), or Cancel.
 */
export function GroupSave(
    props: Readonly<{ data: PriceListData; group: PricingGroupRow; draft: GroupDraft; /** Saving and updating, or showing what couldn't be (its fields wait). */ onRunning?: (running: boolean) => void; onDone: () => void }>,
) {
    const { data, group, draft } = props;
    const saver = usePriceListSaver();
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const edited = !sameDraft(draft, group, data.taxRates);
    const moving = edited ? groupPreview(data, group.id, draft) : [];
    // Its updates asked about first (many items at once; locked prices among them held to confirm).
    const ask = useGroupUpdateConfirm(data);
    // Something typed above can't be taken (the draft has what it had before): not saved until it's put right.
    const typedBad = useDraftInvalid();
    // Its labour changed (a labour cost set): the costs of those it prices change too, updated with it (for those who may change costs).
    const labourMoves = edited && draft.rule.labourMinutes !== group.rule.labourMinutes && hasLabourCost(data.settings);
    const costChanges = labourMoves ? groupCostChanges(data, group.id, draft.rule.labourMinutes) : new Map<string, CostChange>();
    const costed = [...costChanges.values()].map((c) => c.m);
    const withCosts = costed.length > 0 && can(data, "costs.edit");
    // Saving and updating: the list as it was when it started (saved, the draft is the group's, so it would say nothing changes),
    // each row marked as it's updated and gone once done; any turned down stay, marked, until it's closed.
    const [frozen, setFrozen] = useState<{ moving: typeof moving; costs: typeof costChanges } | null>(null);
    const updates = useRowUpdates();
    const stillShown = useStillShown();
    const shown = frozen ?? { moving, costs: costChanges };
    const { onRunning } = props;
    const running = busy !== null || frozen !== null;
    useEffect(() => {
        onRunning?.(running);
        return () => onRunning?.(false);
    }, [running, onRunning]);
    const failed = frozen !== null && busy === null && [...updates.rows.values()].some((r) => r.state === "failed");

    // Like an item's: save and update the prices it moves (one by one, as the person clicking; locked ones asked about first, once),
    // or save only (those that then differ show as to update on its Items tab). With its labour, their costs too, one by one after.
    const save = async (update: boolean, costs = false) => {
        setError(null);
        // Many items at once: asked first (locked prices among them held to confirm).
        if ((update || costs) && !(await ask.saveAndUpdate(update ? moving.map((r) => r.m.uuid) : [], costs ? costed.length : 0))) return;
        if (update || costs) setFrozen({ moving: update ? moving : [], costs: costs ? costChanges : new Map() });
        updates.clear();
        setBusy("Saving...");
        try {
            await saver.updateGroup(group.id, draft);
        } catch (err) {
            setError(err as Error);
            setFrozen(null);
            setBusy(null);
            return;
        }
        // Item by item, each in one save: its price and cost together when both change (one ServiceM8 write, so neither is saved
        // without the other), else the one that does. One turned down doesn't stop the rest (said once they're all done).
        const withPrice = new Set(update ? moving.map((r) => r.m.uuid) : []);
        const withCost = new Set(costs ? costed.map((m) => m.uuid) : []);
        const each = (uuid: string) => {
            const locked = priceLock(data, uuid) !== null;
            if (withPrice.has(uuid) && withCost.has(uuid)) return saver.updatePriceAndCost(uuid, locked);
            return withPrice.has(uuid) ? saver.updateToPricing(uuid, locked) : saver.updateCost(uuid);
        };
        // Its row's price and cost cells marked together.
        const mark = (uuid: string, row: RowUpdate) => {
            if (withPrice.has(uuid)) updates.track("prices")(uuid, row);
            if (withCost.has(uuid)) updates.track("costs")(uuid, row);
        };
        try {
            await updateEach([...new Set([...withPrice, ...withCost])], each, "items", setBusy, mark);
            // Another group since (its window moved on): its draft isn't this one's to clear.
            if (stillShown.current) props.onDone();
        } catch (err) {
            setError(err as Error);
        }
    };

    return (
        // As an item's: each price it moves, then its save bar along the bottom of the editor (edge to edge): what saving does on
        // the left, its buttons on the right (under it only when narrow).
        <>
            {edited || frozen ? <PriceChanges data={data} rows={shown.moving} costs={shown.costs} updates={updates.rows} /> : null}
            <div className="-mx-4 -mb-4 border-t border-brand-500/30 px-3 py-2">
                <DraftBar
                    // Saving or updating: just how far it's got (the list above shows which), on one line.
                    message={
                        busy ? (
                            <span className="inline-flex items-center gap-2">
                                <Spinner className="size-4" /> {busy}
                            </span>
                        ) : failed ? (
                            "Its pricing is saved, but some couldn't be updated: they're marked above."
                        ) : edited || frozen ? (
                            <>
                                {/* Its labour changed (a labour cost set): its items' costs change too (updated with it, or left to update). */}
                                <PriceSummary
                                    prices={shown.moving.length}
                                    costs={withCosts || frozen ? shown.costs.size : 0}
                                    overriding={overridingIn(data, group.id).length}
                                    costsLater={labourMoves && !withCosts && !frozen}
                                />
                                {typedBad ? <span className="block text-xs text-danger">Put right what&apos;s marked above to save.</span> : null}
                            </>
                        ) : (
                            "Change the pricing above to see what happens to prices."
                        )
                    }
                    error={error}
                    actions={
                        // Turned down: closed when they've seen it (they're still to update, on its Pricing and Items tabs).
                        failed ? (
                            <button type="button" className="btn-secondary" onClick={props.onDone}>
                                Close
                            </button>
                        ) : (
                            <>
                                {edited && moving.length ? (
                                    // Update them now, or leave them to update: always in that order (locked ones are asked about first, held).
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        <button
                                            type="button"
                                            className="btn-primary"
                                            disabled={Boolean(busy) || typedBad}
                                            title={`Save the pricing and update the ${plural(moving.length, "price")} it moves${withCosts ? `, and the ${plural(costed.length, "cost")} its labour changes` : ""}`}
                                            onClick={() => void save(true, withCosts)}
                                        >
                                            Save &amp; update
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            disabled={Boolean(busy) || typedBad}
                                            title={`Save the pricing only: its prices${withCosts ? " and costs" : ""} stay as they are, and show as to update on its Items tab`}
                                            onClick={() => void save(false)}
                                        >
                                            Save only
                                        </button>
                                    </div>
                                ) : null}
                                {edited && !moving.length && withCosts ? (
                                    // No price moves, but its labour changes their costs: update them now, or leave them to update.
                                    <>
                                        <button type="button" className="btn-primary" disabled={Boolean(busy) || typedBad} title={`Save the pricing and update the ${plural(costed.length, "cost")} its labour changes`} onClick={() => void save(false, true)}>
                                            Save &amp; update
                                        </button>
                                        <button type="button" className="btn-secondary" disabled={Boolean(busy) || typedBad} title="Save the pricing only: its costs show as to update on its Items tab" onClick={() => void save(false)}>
                                            Save only
                                        </button>
                                    </>
                                ) : null}
                                {edited && !moving.length && !withCosts ? (
                                    <button type="button" className="btn-primary" disabled={Boolean(busy) || typedBad} onClick={() => void save(false)}>
                                        Save pricing
                                    </button>
                                ) : null}
                                <button type="button" className="btn-secondary" disabled={Boolean(busy)} onClick={props.onDone}>
                                    Cancel
                                </button>
                            </>
                        )
                    }
                />
            </div>
        </>
    );
}

/** What saving a group change will cause, beside its Save: how many prices will need updating (its Items tab), and its items overriding it keep theirs. */
function PriceSummary({ prices, costs, overriding, costsLater }: Readonly<{ prices: number; costs: number; overriding: number; costsLater: boolean }>) {
    // Short (beside its buttons): what changes, then, small under it, what doesn't.
    const things = [prices ? itemCount(prices, "price") : "", costs ? itemCount(costs, "cost") : ""].filter(Boolean).join(" and ");
    const text = things ? `${things} ${prices + costs === 1 ? "changes" : "change"}` : "No prices change";
    const notes = [
        overriding ? (overriding === 1 ? "1 overriding item keeps its own." : `${overriding} overriding items keep theirs.`) : "",
        // Their costs change too, but this person can't update them: left to update.
        costsLater ? "Their costs will need updating for their labour." : "",
    ].filter(Boolean);
    return (
        <>
            <span className="font-medium">{text}</span>
            {notes.length ? <span className="block text-xs">{notes.join(" ")}</span> : null}
        </>
    );
}

/** An item's cost as its labour will change it (in its basis, as ServiceM8 keeps it). */
type CostChange = { m: MaterialRow; now: string; next: string };

/**
 * The costs a group's labour time changes (`minutes`, the draft's): those it prices, each its part's cost with its labour then
 * (pricing.ts costWithLabour), where that's another cost. By uuid, in the group's order.
 */
function groupCostChanges(data: PriceListData, groupId: string, minutes: number) {
    const out = new Map<string, CostChange>();
    for (const m of membersOf(data, groupId)) {
        const pricing = data.itemPricing[m.uuid];
        if (!m.active || !pricing || !pricedByGroup(pricing)) continue;
        const withDraft = { ...data, itemPricing: { ...data.itemPricing, [m.uuid]: { ...pricing, labourMinutes: minutes } } };
        const next = costWithLabour(withDraft, m, estimatedPartCost(data, m));
        if (!samePrice(next, m.cost)) out.set(m.uuid, { m, now: m.cost, next });
    }
    return out;
}

/**
 * Each item a group's pricing changes: its price (now -> new, with its tax when that changes too), and with `costs` (its labour
 * changing) its cost, in a column each; an item whose cost alone changes is listed too. Being updated (`updates`, by
 * "prices:uuid" / "costs:uuid"): the row being saved has a spinner, those waiting are faded, a change done says so, a row all done
 * goes, and one turned down says so (why, on hover).
 */
function PriceChanges(
    props: Readonly<{ data: PriceListData; rows: ReturnType<typeof groupPreview>; costs?: ReadonlyMap<string, CostChange>; updates?: ReadonlyMap<string, RowUpdate> }>,
) {
    const { data, rows, costs, updates } = props;
    const fmt = useMoney();
    const prices = new Map(rows.map((r) => [r.m.uuid, r]));
    const stateOf = (what: string, uuid: string) => updates?.get(`${what}:${uuid}`);
    // Its changes there are, each's update (none: not being updated).
    const partsOf = (uuid: string) => [prices.has(uuid) ? stateOf("prices", uuid) : null, costs?.has(uuid) ? stateOf("costs", uuid) : null].filter((s) => s !== null);
    const allDone = (uuid: string) => {
        const parts = partsOf(uuid);
        return parts.length > 0 && parts.every((s) => s?.state === "done");
    };
    const items = [...rows.map((r) => r.m), ...[...(costs?.values() ?? [])].filter((c) => !prices.has(c.m.uuid)).map((c) => c.m)].filter((m) => !allDone(m.uuid));
    if (!items.length) return null;
    const withCosts = Boolean(costs?.size);
    const change = (now: string, next: string, taxNow?: string, taxNext?: string) => (
        <>
            <span className="text-muted">
                {fmt.money(now)}
                {taxNext === taxNow ? null : <span className="text-xs"> {taxNow}</span>}
            </span>{" "}
            → <span className="font-semibold">{fmt.money(next)}</span>
            {taxNext === taxNow ? null : <span className="text-xs text-muted"> {taxNext}</span>}
        </>
    );
    const none = <span className="text-muted">-</span>;
    // A row: on the list's columns (subgrid), so they line up whatever's in them.
    const row = clsx("grid grid-cols-subgrid items-center gap-x-4 px-3 py-1.5", withCosts ? "col-span-3" : "col-span-2");
    return (
        <div>
            {/* One grid for the list: the item, its price, and (its labour changing) its cost; headed by what each column is. */}
            <ul className={clsx("grid divide-y divide-line rounded-md border border-line bg-surface", withCosts ? "grid-cols-[minmax(0,1fr)_auto_auto]" : "grid-cols-[minmax(0,1fr)_auto]")}>
                <li className={clsx(row, "text-xs font-medium text-muted")}>
                    <span>Item</span>
                    <span className="text-right">Price</span>
                    {withCosts ? <span className="text-right">Cost</span> : null}
                </li>
                {items.map((m) => {
                    const p = prices.get(m.uuid);
                    const c = costs?.get(m.uuid);
                    const parts = partsOf(m.uuid);
                    const updating = parts.some((s) => s?.state === "updating");
                    const waiting = !updating && parts.some((s) => s?.state === "waiting");
                    // A change's cell: as it is, or done, or turned down.
                    const cell = (what: string, shownChange: ReactNode) => {
                        const state = stateOf(what, m.uuid);
                        if (state?.state === "done") return <span className="text-xs text-muted">Updated</span>;
                        if (state?.state === "failed") {
                            return (
                                <span className="text-xs text-danger" title={state.error}>
                                    Couldn&apos;t update
                                </span>
                            );
                        }
                        return shownChange;
                    };
                    return (
                        <li key={m.uuid} className={clsx(row, waiting && "opacity-50")}>
                            <span className="flex min-w-0 items-center gap-2">
                                {updating ? <Spinner className="size-3.5 shrink-0" /> : null}
                                <span className="truncate">{itemName(data, m)}</span>
                            </span>
                            <span className="text-right whitespace-nowrap tabular-nums">{p ? cell("prices", change(p.now, p.next, p.taxNow, p.taxNext)) : none}</span>
                            {withCosts ? <span className="text-right whitespace-nowrap tabular-nums">{c ? cell("costs", change(c.now, c.next)) : none}</span> : null}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

/** "1 other item", "3 other items". */
export const itemCount = (n: number, noun: string) => plural(n, noun);
